import { useCallback, useEffect, useState, useRef } from 'react'
import debounce from 'lodash.debounce'
import getConfig from 'next/config'

import useGeocodingService from '@/components/search/useGeocodingService'
import { useSearchTracking } from '@/hooks/useSearchTracking'
import { useFetchPaginationParkings } from '@/service/apiHooks/useFetchPaginationParkings'

interface UseSearchBoxParams {
  market: string
  t: (key: string, options?: any) => string
  skipMapboxSuggestions?: boolean
}

export const useSearchBox = ({
  market,
  t,
  skipMapboxSuggestions = false
}: UseSearchBoxParams) => {
  const {
    publicRuntimeConfig: { MAP_BOX_KEY }
  } = getConfig()
  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState<any>([])
  const [searchHistory, setSearchHistory] = useState<any[]>([])
  const geocodingService = useGeocodingService()
  const { trackSearchQuery } = useSearchTracking()

  const isOptionSelected = useRef(false)
  const searchFunctionRef = useRef<any>(null)
  const optionsRef = useRef<any[]>([])

  useEffect(() => {
    optionsRef.current = options
  }, [options])

  const { parkings } = useFetchPaginationParkings({
    params: {
      size: 150,
      page: 0,
      visible: true
    }
  })

  useEffect(() => {
    try {
      const storedHistory = localStorage.getItem('searchHistory')
      if (storedHistory) {
        setSearchHistory(JSON.parse(storedHistory))
      }
    } catch (error) {
      console.error('Error loading search history:', error)
    }
  }, [])

  const saveToSearchHistory = useCallback(
    (option: any) => {
      if (!option) return

      isOptionSelected.current = true

      const newHistory = [
        option,
        ...searchHistory.filter(item => item.label !== option.label)
      ].slice(0, 10)

      setSearchHistory(newHistory)

      try {
        localStorage.setItem('searchHistory', JSON.stringify(newHistory))
      } catch (error) {
        console.error('Error saving search history:', error)
      }

      setTimeout(() => {
        isOptionSelected.current = false
      }, 300)
    },
    [searchHistory]
  )

  const formatOptionLabel = (suggestion: any): string => {
    if (suggestion.feature_type === 'place') {
      return suggestion.name
    }

    const name = suggestion.name || ''
    const fullAddress = suggestion.full_address || ''
    const formattedAddress = fullAddress
      .replace(/,\s*\d{2}-\d{3}\s*/, ', ')
      .replace(/,\s*Polska$/, '')
      .replace(/,\s*województwo\s+\w+$/, '')

    const isNameInAddress = formattedAddress
      .toLowerCase()
      .includes(name.toLowerCase())

    return isNameInAddress
      ? formattedAddress.trim()
      : `${name}, ${formattedAddress}`.trim()
  }

  const fetchCoordinates = async (suggestion: any) => {
    if (!geocodingService.current) return [0, 0]

    const query =
      suggestion.feature_type === 'place'
        ? suggestion.name
        : suggestion.full_address

    if (!query) return [0, 0]

    try {
      const trimmedQuery = query.substring(0, 100)

      const response = await geocodingService.current
        .forwardGeocode({
          query: trimmedQuery,
          limit: 1,
          countries: [market.toLowerCase()],
          language: ['pl']
        })
        .send()

      const feature = response.body.features[0]
      return feature ? feature.center : [0, 0]
    } catch (error) {
      console.error('Error fetching coordinates:', error)
      return [0, 0]
    }
  }

  const filterParkings = (value: string) => {
    if (!value || value.length <= 3 || !parkings || parkings.length === 0) {
      return []
    }

    const lowerCaseValue = value.toLowerCase()
    return parkings
      .filter(
        parking =>
          parking.shortDisplayName?.toLowerCase().includes(lowerCaseValue) ||
          parking.address?.toLowerCase().includes(lowerCaseValue)
      )
      .slice(0, 2)
      .map(parking => ({
        id: parking.uuid,
        label: `${parking.shortDisplayName} - ${parking.address}`,
        value: [parking.lon, parking.lat],
        isAirport: false,
        metadata: {
          type: 'parking',
          country: market,
          relevance: 1.0
        },
        isParking: true
      }))
  }

  useEffect(() => {
    searchFunctionRef.current = debounce(async (value: string) => {
      if (!value || value.length <= 3) {
        setOptions([])
        return
      }

      trackSearchQuery(value, 0, market)

      const filteredParkings = filterParkings(value)

      const historyMatches = searchHistory
        .filter(item => item.label.toLowerCase().includes(value.toLowerCase()))
        .slice(0, 3)

      let mapboxResults: any[] = []

      if (!skipMapboxSuggestions) {
        try {
          const params = {
            q: value.trim().substring(0, 100),
            access_token: MAP_BOX_KEY,
            session_token: 'cb49bec2-be8d-4401-a9f9-0ddca22c56a9',
            language: 'pl',
            limit: '10',
            types: 'locality,place,neighborhood,address,poi',
            country: market
          }

          const queryString = new URLSearchParams(params).toString()
          const url = `https://api.mapbox.com/search/searchbox/v1/suggest?${queryString}`

          const response = await fetch(url)
          if (!response.ok) {
            throw new Error(`Mapbox API error: ${response.status}`)
          }

          const data = await response.json()
          const suggestions = data.suggestions || []

          const uniqueSuggestions = suggestions.reduce(
            (acc: any[], suggestion: any) => {
              const isDuplicate = acc.some(
                item => item.full_address === suggestion.full_address
              )
              if (!isDuplicate) {
                acc.push(suggestion)
              }
              return acc
            },
            []
          )

          mapboxResults = await Promise.all(
            uniqueSuggestions.map(async (suggestion: any) => ({
              id: Math.random().toString(36).substr(2, 9),
              label: formatOptionLabel(suggestion),
              value: await fetchCoordinates(suggestion),
              isAirport: false,
              metadata: {
                type: suggestion.feature_type,
                country: suggestion.country || market,
                relevance: suggestion.relevance || 1.0
              }
            }))
          )
        } catch (error) {
          console.error('Error fetching suggestions:', error)
        }
      }

      const combinedResults = [
        ...filteredParkings,
        ...historyMatches,
        ...mapboxResults
      ]
        .filter(
          (option, index, self) =>
            index === self.findIndex(o => o.label === option.label)
        )
        .slice(0, 10)

      setOptions(combinedResults)

      trackSearchQuery(value, combinedResults.length, market)

      localStorage.setItem(
        'dropdownLocationsList',
        JSON.stringify(combinedResults)
      )
      localStorage.setItem('filteredParkings', JSON.stringify(filteredParkings))
    }, 300)

    return () => {
      if (searchFunctionRef.current) {
        searchFunctionRef.current.cancel()
      }
    }
  }, [
    MAP_BOX_KEY,
    market,
    searchHistory,
    parkings,
    skipMapboxSuggestions,
    trackSearchQuery
  ])

  const setInputValueWrapper = useCallback((value: string) => {
    setInputValue(value)
  }, [])

  useEffect(() => {
    if (searchFunctionRef.current && !isOptionSelected.current) {
      searchFunctionRef.current(inputValue)
    }
  }, [inputValue])

  const noOptionsMessage = inputValue?.length > 3 ? t('noResults.title') : null

  const getFirstOption = useCallback(() => {
    if (optionsRef.current.length > 0 && inputValue.length > 3) {
      return optionsRef.current[0]
    }
    return null
  }, [inputValue])

  return {
    setInputValue: setInputValueWrapper,
    noOptionsMessage,
    inputValue,
    options,
    getFirstOption,
    saveToSearchHistory
  }
}
